import { allCountries, US } from 'country-region-data'

import { CONTROL_TYPES as FORMKIT_CONTROL_TYPES } from '@components/formkit-form-renderer'

export const FORM_CATEGORY_KEY = Symbol('FORM_CATEGORY_KEY')
export const TOP_LEVEL_FIELD_TYPES_SELECTED_COUNT_MAP_KEY = Symbol('TOP_LEVEL_FIELD_TYPES_SELECTED_COUNT_MAP')

export const FIELD_TYPES = {
  TEXT: 'Text (single line)',
  TEXTAREA: 'Text area (multi-line)',
  DROPDOWNLIST: 'Drop-down List',
  CHECKBOXLIST: 'Checkbox List',
  NUMBER: 'Number',
  COMPANY: 'Company',
  DATE: 'Date',
  COUNTRY: 'Country',
  STATE: 'State',
  CURRENCY: 'Currency',
  EMAIL: 'Email',
  DEALVALUE: 'Deal Value',
}

export const DISABLED_FIELD_TYPES = {
  [FIELD_TYPES.EMAIL]: true,
}

export const TOP_LEVEL_FIELD_TYPES = {
  [FIELD_TYPES.COMPANY]: true,
  [FIELD_TYPES.DEALVALUE]: true,
}

export const TOP_LEVEL_FIELD_TYPES_MAX_ALLOWED_MAP = {
  [FIELD_TYPES.COMPANY]: 1,
  [FIELD_TYPES.DEALVALUE]: 1,
}

export const OPERATORS = {
  EQUAL_TO: 'EQUAL_TO',
  EQUAL_TO_ONE_OF: 'EQUAL_TO_ONE_OF',
  GREATER_THAN: 'GREATER_THAN',
  LESS_THAN: 'LESS_THAN',
  CONTAINS_SUBSTRING: 'CONTAINS_SUBSTRING',
  BEFORE: 'BEFORE',
  AFTER: 'AFTER',
  ITEMS_EQUAL: 'ITEMS_EQUAL',
  ITEMS_CONTAIN_ONE_OF: 'ITEMS_CONTAIN_ONE_OF',
  COMPANY_EQUAL_TO: 'COMPANY_EQUAL_TO',
  COMPANY_EQUAL_TO_ONE_OF: 'COMPANY_EQUAL_TO_ONE_OF',
}

export const OPERATOR_LABELS_MAP = {
  [OPERATORS.EQUAL_TO]: 'is equal to',
  [OPERATORS.EQUAL_TO_ONE_OF]: 'contains',
  [OPERATORS.GREATER_THAN]: 'is greater than',
  [OPERATORS.LESS_THAN]: 'is less than',
  [OPERATORS.CONTAINS_SUBSTRING]: 'contains',
  [OPERATORS.BEFORE]: 'is before',
  [OPERATORS.AFTER]: 'is after',
  [OPERATORS.ITEMS_EQUAL]: 'is equal to',
  [OPERATORS.ITEMS_CONTAIN_ONE_OF]: 'contains',
  [OPERATORS.COMPANY_EQUAL_TO]: 'is equal to',
  [OPERATORS.COMPANY_EQUAL_TO_ONE_OF]: 'contains',
}

export const OPERATOR_SCHEMA_KEYWORDS_MAP = {
  [OPERATORS.EQUAL_TO]: 'const',
  [OPERATORS.EQUAL_TO_ONE_OF]: 'enum',
  [OPERATORS.GREATER_THAN]: 'greaterThan',
  [OPERATORS.LESS_THAN]: 'lessThan',
  [OPERATORS.CONTAINS_SUBSTRING]: 'containsSubstring',
  [OPERATORS.BEFORE]: 'dateBefore',
  [OPERATORS.AFTER]: 'dateAfter',
  [OPERATORS.ITEMS_EQUAL]: 'itemsEqual',
  [OPERATORS.ITEMS_CONTAIN_ONE_OF]: 'itemsContainOneOf',
  [OPERATORS.COMPANY_EQUAL_TO]: 'companyEqual',
  [OPERATORS.COMPANY_EQUAL_TO_ONE_OF]: 'companyEqualToOneOf',
}

export const FORMKIT_OPERATOR_FUNCTIONS_MAP = {
  [OPERATORS.EQUAL_TO]: 'equalTo',
  [OPERATORS.EQUAL_TO_ONE_OF]: 'equalToOneOf',
  [OPERATORS.GREATER_THAN]: 'greaterThan',
  [OPERATORS.LESS_THAN]: 'lessThan',
  [OPERATORS.CONTAINS_SUBSTRING]: 'containsSubstring',
  [OPERATORS.BEFORE]: 'dateBefore',
  [OPERATORS.AFTER]: 'dateAfter',
  [OPERATORS.ITEMS_EQUAL]: 'itemsEqual',
  [OPERATORS.ITEMS_CONTAIN_ONE_OF]: 'itemsContainOneOf',
  [OPERATORS.COMPANY_EQUAL_TO]: 'companyEqual',
  [OPERATORS.COMPANY_EQUAL_TO_ONE_OF]: 'companyEqualToOneOf',
}

export const FIELD_TYPE_FORMKIT_CONTROL_TYPES_MAP = {
  [FIELD_TYPES.TEXT]: FORMKIT_CONTROL_TYPES.SINGLE_LINE_TEXT,
  [FIELD_TYPES.TEXTAREA]: FORMKIT_CONTROL_TYPES.MULTI_LINE_TEXT,
  [FIELD_TYPES.DROPDOWNLIST]: FORMKIT_CONTROL_TYPES.DROPDOWN_LIST,
  [FIELD_TYPES.CHECKBOXLIST]: FORMKIT_CONTROL_TYPES.CHECKBOX_LIST,
  [FIELD_TYPES.NUMBER]: FORMKIT_CONTROL_TYPES.NUMBER,
  [FIELD_TYPES.COMPANY]: FORMKIT_CONTROL_TYPES.COMPANY,
  [FIELD_TYPES.DATE]: FORMKIT_CONTROL_TYPES.DATE,
  [FIELD_TYPES.COUNTRY]: FORMKIT_CONTROL_TYPES.COUNTRY,
  [FIELD_TYPES.STATE]: FORMKIT_CONTROL_TYPES.STATE,
  [FIELD_TYPES.CURRENCY]: FORMKIT_CONTROL_TYPES.CURRENCY,
  [FIELD_TYPES.DEALVALUE]: FORMKIT_CONTROL_TYPES.DEAL_VALUE,
  [FIELD_TYPES.EMAIL]: FORMKIT_CONTROL_TYPES.EMAIL,
}

export const FIELD_TYPE_LABEL_MAP = {
  [FIELD_TYPES.TEXT]: 'text',
  [FIELD_TYPES.TEXTAREA]: 'textarea',
  [FIELD_TYPES.DROPDOWNLIST]: 'dropdown',
  [FIELD_TYPES.CHECKBOXLIST]: 'checkbox',
  [FIELD_TYPES.NUMBER]: 'number',
  [FIELD_TYPES.COMPANY]: 'company',
  [FIELD_TYPES.DATE]: 'date',
  [FIELD_TYPES.COUNTRY]: 'country',
  [FIELD_TYPES.STATE]: 'state',
  [FIELD_TYPES.CURRENCY]: 'currency',
  [FIELD_TYPES.DEALVALUE]: 'deal value',
  [FIELD_TYPES.EMAIL]: 'email',
}

export const FIELD_TYPE_OPERATORS_MAP = {
  [FIELD_TYPES.TEXT]: [
    OPERATORS.EQUAL_TO,
    OPERATORS.CONTAINS_SUBSTRING,
  ],
  [FIELD_TYPES.TEXTAREA]: [
    OPERATORS.EQUAL_TO,
    OPERATORS.CONTAINS_SUBSTRING,
  ],
  [FIELD_TYPES.DROPDOWNLIST]: [
    OPERATORS.EQUAL_TO,
  ],
  [FIELD_TYPES.CHECKBOXLIST]: [
    OPERATORS.ITEMS_EQUAL,
    OPERATORS.ITEMS_CONTAIN_ONE_OF,
  ],
  [FIELD_TYPES.NUMBER]: [
    OPERATORS.LESS_THAN,
    OPERATORS.EQUAL_TO,
    OPERATORS.GREATER_THAN,
  ],
  [FIELD_TYPES.COMPANY]: [
    OPERATORS.COMPANY_EQUAL_TO,
    OPERATORS.COMPANY_EQUAL_TO_ONE_OF,
  ],
  [FIELD_TYPES.DATE]: [
    OPERATORS.BEFORE,
    OPERATORS.EQUAL_TO,
    OPERATORS.AFTER,
  ],
  [FIELD_TYPES.COUNTRY]: [
    OPERATORS.EQUAL_TO,
    OPERATORS.EQUAL_TO_ONE_OF,
  ],
  [FIELD_TYPES.STATE]: [
    OPERATORS.EQUAL_TO,
    OPERATORS.EQUAL_TO_ONE_OF,

  ],
  [FIELD_TYPES.CURRENCY]: [
    OPERATORS.LESS_THAN,
    OPERATORS.EQUAL_TO,
    OPERATORS.GREATER_THAN,
  ],
  [FIELD_TYPES.DEALVALUE]: [
    OPERATORS.LESS_THAN,
    OPERATORS.EQUAL_TO,
    OPERATORS.GREATER_THAN,
  ],
}

export const FIELD_TYPE_ICONS_MAP = {
  [FIELD_TYPES.COMPANY]: 'apartment',
  [FIELD_TYPES.DATE]: 'calendar_month',
  [FIELD_TYPES.COUNTRY]: 'flag',
  [FIELD_TYPES.STATE]: 'location_on',
  [FIELD_TYPES.CURRENCY]: 'attach_money',
  [FIELD_TYPES.DEALVALUE]: 'attach_money',
}

export const FORM_STATUS = {
  VISIBLE: 'Visible',
  HIDDEN: 'Hidden',
}

export const COUNTRY_DROPDOWN_OPTIONS = allCountries.map(c => c[0]).map(c => ({ id: c, name: c }))

export const US_STATE_DROPDOWN_OPTIONS =  US[2].map(s => s[0]).map(s => ({ id: s, name: s }))